import React, { useEffect, useState, useCallback } from "react";
import { Pagination } from "@mui/material";
import axiosInstance from "../../../../components/axiosInstance";
import { API_URLS } from "../../../../apiUrl";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CashierDeposit {
  name: string;
  amount: number;
  date: string;
  user_name: string;
  email: string;
}

interface CollectorRow {
  collector_name: string;
  username: string;
  total_mapped_amount: string;
  total_consumers_mapped: string;
  total_deposited_by_cashiers: string;
  deposited_cashiers_data: CashierDeposit[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getToday = () => new Date().toISOString().split("T")[0];

const getOneMonthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split("T")[0];
};

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const fmtDate = (raw: string) => {
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── Cashier Modal ────────────────────────────────────────────────────────────

const CashierModal: React.FC<{ row: CollectorRow; onClose: () => void }> = ({ row, onClose }) => {
  const deposits = row.deposited_cashiers_data || [];
  const total = deposits.reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-[580px] max-w-[95vw] z-50 overflow-hidden">
        <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold text-base">Cashier Deposit Details</h3>
            <p className="text-cyan-100 text-xs mt-0.5">
              Collector: <span className="font-medium text-white">{row.collector_name}</span>
              &nbsp;·&nbsp;
              <span className="font-medium text-white">@{row.username}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition"
          >
            ✕
          </button>
        </div>

        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cashier</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Username</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deposits.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                    No deposit records found
                  </td>
                </tr>
              ) : (
                deposits.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center text-xs font-bold">
                          {c.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="font-medium text-gray-800 text-xs">{c.name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">@{c.user_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-emerald-600 text-xs">
                      ₹ {fmt(Number(c.amount || 0))}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{fmtDate(c.date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t flex justify-between items-center">
          <span className="text-xs text-gray-500">{deposits.length} transaction(s)</span>
          <span className="text-sm font-bold text-gray-800">
            Total: <span className="text-emerald-600">₹ {fmt(total)}</span>
          </span>
        </div>
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CollectorReport: React.FC = () => {
  const [data, setData] = useState<CollectorRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(getOneMonthAgo());
  const [endDate, setEndDate] = useState(getToday());
  const [dateError, setDateError] = useState("");

  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const limit = 50;

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (pageNumber: number, start: string, end: string) => {
    setLoading(true);
    try {
      const res = await axiosInstance.post(API_URLS.collectorReport, {
        start_date: start,
        end_date: end,
        page: pageNumber,
        limit,
      });

      const payload = res.data?.data;

      if (Array.isArray(payload)) {
        setData(payload);
        setTotalPages(res.data?.pagination?.lastPage || 1);
      } else if (payload?.rows && Array.isArray(payload.rows)) {
        setData(payload.rows);
        setTotalPages(payload?.pagination?.lastPage || 1);
      } else if (payload && typeof payload === "object") {
        setData([payload as CollectorRow]);
        setTotalPages(1);
      } else {
        setData([]);
        setTotalPages(1);
      }
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong. Please try again.";

      toast.error(apiMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { fontSize: "13px" },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchData(1, startDate, endDate);
  }, []);

  // ─── Start date change ───────────────────────────────────────────────────────

  const handleStartDateChange = (val: string) => {
    const today = getToday();

    if (val > today) {
      setDateError("Start date cannot be a future date.");
      return;
    }

    setStartDate(val);

    // If current end date is before new start date → push end to start + 1 day
    let newEnd = endDate;
    if (endDate <= val) {
      const d = new Date(val);
      d.setDate(d.getDate() + 1);
      newEnd = d.toISOString().split("T")[0];
      if (newEnd > today) newEnd = today;
      setEndDate(newEnd);
    }

    setDateError("");
    setPage(1);
    fetchData(1, val, newEnd);
  };

  // ─── End date change ─────────────────────────────────────────────────────────

  const handleEndDateChange = (val: string) => {
    const today = getToday();

    if (val > today) {
      setDateError("End date cannot be a future date.");
      return;
    }

    if (val < startDate) {
      setDateError("End date cannot be before start date.");
      setEndDate(val);
      return;
    }

    setEndDate(val);
    setDateError("");
    setPage(1);
    fetchData(1, startDate, val);
  };

  // ─── Reset ───────────────────────────────────────────────────────────────────

  const handleReset = () => {
    const s = getOneMonthAgo();
    const e = getToday();
    setStartDate(s);
    setEndDate(e);
    setDateError("");
    setPage(1);
    fetchData(1, s, e);
  };

  // ─── Pagination ──────────────────────────────────────────────────────────────

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
    fetchData(value, startDate, endDate);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="p-5 bg-white rounded-lg shadow relative">
      <ToastContainer />

      {/* Modal */}
      {activeIndex !== null && data[activeIndex] && (
        <CashierModal
          row={data[activeIndex]}
          onClose={() => setActiveIndex(null)}
        />
      )}

      <h2 className="text-lg font-semibold mb-4">Collector Report</h2>

      {/* Filter */}
      <div className="flex flex-wrap items-end gap-3 mb-1">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Start Date</label>
          <input
            type="date"
            value={startDate}
            max={getToday()}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={getToday()}
            onChange={(e) => handleEndDateChange(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
        </div>

        <button
          onClick={handleReset}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 rounded-md text-sm transition"
        >
          Reset <i className="ri-restart-line"></i>
        </button>
      </div>

      {dateError && (
        <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
          ⚠️ {dateError}
        </p>
      )}

      {loading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      <div className="overflow-x-auto mt-3">
        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border text-left">Collector</th>
              <th className="p-2 border text-left">Username</th>
              <th className="p-2 border text-center">Consumers</th>
              <th className="p-2 border text-right">Mapped Amount</th>
              <th className="p-2 border text-right">Deposited</th>
            </tr>
          </thead>

          <tbody>
            {data.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="text-center p-6 text-gray-400">
                  No data found for the selected date range.
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const mapped = Number(item.total_mapped_amount || 0);
                const deposited = Number(item.total_deposited_by_cashiers || 0);
                return (
                  <tr key={index} className="hover:bg-gray-50 transition">
                    {/* Collector */}
                    <td className="p-2 border font-medium text-gray-800">
                      {item.collector_name || "—"}
                    </td>

                    {/* Username */}
                    <td className="p-2 border text-gray-500 text-xs">
                      @{item.username || "—"}
                    </td>

                    {/* Consumers */}
                    <td className="p-2 border text-center">
                      {item.total_consumers_mapped || 0}
                    </td>

                    {/* Mapped Amount */}
                    <td className="p-2 border text-right font-semibold text-blue-600">
                      ₹ {fmt(mapped)}
                    </td>

                    {/* Deposited + info */}
                    <td className="p-2 border text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-semibold text-gray-800">
                          ₹ {fmt(deposited)}
                        </span>
                        <button
                          onClick={() =>
                            setActiveIndex(activeIndex === index ? null : index)
                          }
                          title="View cashier breakdown"
                          className="w-4 h-4 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 text-white text-[10px] font-bold transition shrink-0"
                        >
                          i
                        </button>
                      </div>
                    </td>
                    
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />
        </div>
      )}
    </div>
  );
};

export default CollectorReport;